import React, { useState, useEffect } from "react";
import {
  Calendar, User, Phone, Mail, Package, Gift, MapPin, FileText,
  DollarSign, UserCheck, X, Check, Plus, Trash2, Loader2
} from "lucide-react";

const BookingModal = ({ isOpen, onClose, onSave, booking, selectedDate, salesExecutives, onAddExecutive, onDeleteExecutive }) => {
  const [formData, setFormData] = useState({
    date: "", customer_name: "", customer_phone: "", customer_email: "",
    package: "", occasion: "", pincode: "", address: "", sales_executive: "",
    booking_status: "confirmed", discount_percent: 0, notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [showAddExec, setShowAddExec] = useState(false);
  const [newExecName, setNewExecName] = useState("");

  const occasions = ['Birthday', 'Anniversary', 'Baby Shower', 'Housewarming', 'Engagement', 'Retirement', 'Other'];
  const discountOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
  const packagePrices = { Basic: 3999, Classic: 5999, Premium: 8999, Luxury: 14999 };

  const handleAddExec = async () => {
    if (!newExecName.trim()) return;
    await onAddExecutive(newExecName.trim());
    setNewExecName("");
    setShowAddExec(false);
  };

  useEffect(() => {
    if (booking) {
      setFormData({
        date: booking.date || "", customer_name: booking.customer_name || "",
        customer_phone: booking.customer_phone || "", customer_email: booking.customer_email || "",
        package: booking.package || "", occasion: booking.occasion || "",
        pincode: booking.pincode || "", address: booking.address || "", sales_executive: booking.sales_executive || "",
        booking_status: booking.booking_status || "confirmed",
        discount_percent: booking.discount_percent || 0, notes: booking.notes || ""
      });
    } else if (selectedDate) {
      setFormData({
        date: selectedDate, customer_name: "", customer_phone: "", customer_email: "",
        package: "", occasion: "", pincode: "", address: "", sales_executive: "",
        booking_status: "confirmed", discount_percent: 0, notes: ""
      });
    }
  }, [booking, selectedDate]);

  const calculateFinalAmount = () => {
    const original = packagePrices[formData.package] || 0;
    const discount = formData.discount_percent || 0;
    return original - (original * discount / 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSave(formData, booking?.id);
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto" data-testid="booking-modal">
      <div className="bg-white rounded-3xl shadow-xl p-6 w-full max-w-lg my-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
            {booking ? "Edit Booking" : "Add New Booking"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} className="text-[#475569]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
                <Calendar size={14} /> Date *
              </label>
              <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
                <UserCheck size={14} /> Sales Executive
              </label>
              <div className="flex gap-2">
                <select value={formData.sales_executive} onChange={(e) => setFormData({ ...formData, sales_executive: e.target.value })}
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm">
                  <option value="">Select Executive</option>
                  {salesExecutives.map(exec => (
                    <option key={exec.id} value={exec.display}>{exec.display}</option>
                  ))}
                </select>
                <button type="button" onClick={() => setShowAddExec(!showAddExec)}
                  className="px-3 py-2.5 rounded-xl bg-[#E83E8C]/10 text-[#E83E8C] hover:bg-[#E83E8C]/20 transition-colors" title="Add New Executive">
                  <Plus size={16} />
                </button>
              </div>
              {showAddExec && (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <input type="text" value={newExecName} onChange={(e) => setNewExecName(e.target.value)}
                      placeholder="Enter name (e.g. Rahul)"
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExec())} />
                    <button type="button" onClick={handleAddExec}
                      className="px-3 py-2 rounded-xl bg-[#25D366] text-white text-sm font-medium hover:opacity-90">Add</button>
                  </div>
                  {salesExecutives.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-2 space-y-1 max-h-32 overflow-y-auto">
                      {salesExecutives.map(exec => (
                        <div key={exec.id} className="flex items-center justify-between px-2 py-1 text-sm">
                          <span className="text-[#1E1B4B]">{exec.display}</span>
                          <button type="button" onClick={() => onDeleteExecutive(exec.id)} className="text-red-400 hover:text-red-600 p-1" title="Delete executive">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><User size={14} /> Customer Name</label>
            <input type="text" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" placeholder="e.g. Rahul Sharma" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><Phone size={14} /> Phone</label>
              <input type="tel" value={formData.customer_phone} onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" placeholder="9876543210" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><Mail size={14} /> Email</label>
              <input type="email" value={formData.customer_email} onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" placeholder="email@example.com" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><Gift size={14} /> Occasion</label>
              <select value={formData.occasion} onChange={(e) => setFormData({ ...formData, occasion: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm">
                <option value="">Select Occasion</option>
                {occasions.map(occ => <option key={occ} value={occ}>{occ}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><Package size={14} /> Package</label>
              <select value={formData.package} onChange={(e) => setFormData({ ...formData, package: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm">
                <option value="">Select Package</option>
                <option value="Basic">Basic - ₹3,999</option>
                <option value="Classic">Classic - ₹5,999</option>
                <option value="Premium">Premium - ₹8,999</option>
                <option value="Luxury">Luxury - ₹14,999</option>
              </select>
            </div>
          </div>

          {/* Discount Section */}
          <div className="bg-gradient-to-r from-[#E83E8C]/5 to-[#FF9F1C]/5 rounded-xl p-4 border border-[#E83E8C]/10">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><DollarSign size={14} /> Discount</label>
                <select value={formData.discount_percent} onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm bg-white">
                  {discountOptions.map(d => (
                    <option key={d} value={d}>{d}% {d === 0 ? '(No Discount)' : 'OFF'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1E1B4B] mb-2">Final Amount</label>
                <div className="px-3 py-2.5 rounded-xl bg-white border border-gray-200">
                  {formData.package ? (
                    <div className="flex items-center gap-2">
                      {formData.discount_percent > 0 && (
                        <span className="text-sm text-gray-400 line-through">₹{packagePrices[formData.package]}</span>
                      )}
                      <span className="text-lg font-bold text-[#25D366]">₹{calculateFinalAmount().toLocaleString()}</span>
                      {formData.discount_percent > 0 && (
                        <span className="text-xs bg-[#E83E8C] text-white px-2 py-0.5 rounded-full">{formData.discount_percent}% OFF</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Select package</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><MapPin size={14} /> Pincode</label>
            <input type="text" maxLength={6} value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" placeholder="e.g. 400001" data-testid="admin-pincode-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><MapPin size={14} /> Address</label>
            <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm resize-none" rows={2} placeholder="Full address" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><FileText size={14} /> Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm resize-none" rows={2} placeholder="Any special notes..." />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-[#475569] font-medium hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              {booking ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
